import {
  Component,
  computed,
  input,
  output,
  signal,
  inject,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Persona } from 'src/app/domain/model/persona/persona';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { environment } from 'src/environments/environment';

/**
 * DUMB COMPONENT: AsesorCard
 *
 * Muestra la información del asesor/consultor seleccionado.
 * Solo presenta datos, no tiene lógica de negocio.
 *
 * @example
 * <app-asesor-card
 *   [asesor]="asesorSeleccionado()"
 *   [mostrarBotonBuscar]="!esAsesor()"
 *   (buscar)="abrirBusqueda()" />
 */
@Component({
  selector: 'app-asesor-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FieldsetModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TooltipModule
  ],
  template: `
    <p-fieldset legend="Consultor" [toggleable]="true" styleClass="mb-4">
      <!-- Fila 1: Tipo ID + Número + Lupa -->
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 mb-4">
        <div class="sm:col-span-6 lg:col-span-4">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Identificación</label
          >
          <p-dropdown
            [ngModel]="tipoIdentificacion()"
            [options]="tiposIdentificacion()"
            optionLabel="descripcion"
            optionValue="codigo"
            placeholder="--Seleccione--"
            [disabled]="true"
            [style]="{ width: '100%' }"
          >
          </p-dropdown>
        </div>
        <div class="sm:col-span-6 lg:col-span-4 flex items-end gap-2 min-w-0">
          <input
            pInputText
            [value]="numeroIdentificacion()"
            readonly
            class="w-full min-w-0"
          />
          @if (mostrarBotonBuscar()) {
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px] flex-shrink-0"
              pTooltip="Abrir buscador"
              (click)="buscar.emit()"
            ></button>
          }
        </div>
      </div>

      <!-- Filas de datos (solo si hay asesor) -->
      @if (asesor()) {
        @if (!esPersonaJuridica()) {
          <!-- Persona Natural -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >1er nombre</label
              >
              <input
                pInputText
                [value]="asesor()?.dsnombre1 || ''"
                readonly
                class="w-full"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >2do nombre</label
              >
              <input
                pInputText
                [value]="asesor()?.dsnombre2 || ''"
                readonly
                class="w-full"
              />
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >1er apellido</label
              >
              <input
                pInputText
                [value]="asesor()?.dsapellido1 || ''"
                readonly
                class="w-full"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >2do apellido</label
              >
              <input
                pInputText
                [value]="asesor()?.dsapellido2 || ''"
                readonly
                class="w-full"
              />
            </div>
          </div>
        } @else {
          <!-- Persona Jurídica (NIT) -->
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Nombre comercial</label
              >
              <input
                pInputText
                [value]="
                  asesor()?.dsnombre_comercial || asesor()?.dsnombre_legal || ''
                "
                readonly
                class="w-full"
              />
            </div>
          </div>
        }
      }
    </p-fieldset>
  `
})
export class AsesorCardComponent implements OnInit {
  // === Inputs ===
  asesor = input<Persona | null>(null);
  mostrarBotonBuscar = input<boolean>(true);

  // === Outputs ===
  buscar = output<void>();

  // === Datos (cargados desde BD) ===
  private readonly catalogoRepository = inject(CatalogoRepository);
  tiposIdentificacion = signal<CatalogoOption[]>([]);

  // === Computed (lógica de presentación) ===
  tipoIdentificacion = computed(
    () => this.asesor()?.cdtipo_identificacion || ''
  );
  numeroIdentificacion = computed(() => this.asesor()?.nmidentificacion || '');
  esPersonaJuridica = computed(
    () => this.asesor()?.cdtipo_identificacion === 'N'
  );

  ngOnInit(): void {
    this.cargarTiposIdentificacion();
  }

  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando tipos de identificación:', error);
      }
    }
  }
}
